<template>
  <div class="page-shell education-home">
    <header class="navbar">
      <div class="page-container navbar-inner">
        <div class="navbar-left">
          <a
            class="brand"
            href="#"
            @click.prevent="handleNav('home-section')"
          >
            {{ brandName }}
          </a>
          <a
            v-for="item in navLinks"
            :key="item.id"
            href="#"
            :class="{ 'is-active': activeSectionId === item.id }"
            @click.prevent="handleNav(item.id)"
          >
            {{ item.label }}
          </a>
        </div>
      </div>
    </header>

    <section
      id="home-section"
      class="hero-section"
    >
      <div
        class="banner-wrap"
        @touchstart="handleTouchStart"
        @touchmove="handleTouchMove"
        @touchend="handleTouchEnd"
        @mousedown="handlePointerStart"
        @mousemove="handlePointerMove"
        @mouseup="handlePointerEnd"
        @mouseleave="handlePointerLeave"
      >
        <el-carousel
          ref="heroCarousel"
          class="banner"
          trigger="click"
          arrow="never"
          indicator-position="outside"
          :autoplay="false" @change="handleHeroCarouselChange"
        >
          <el-carousel-item
            v-for="(item, index) in swiperImages"
            :key="item.src"
          >
            <div
              class="banner-slide"
              role="button"
              tabindex="0"
              @click="handleBannerClick(item)"
              @keydown.enter.prevent="handleBannerClick(item)"
              @keydown.space.prevent="handleBannerClick(item)"
            >
              <video
                v-if="index === 0"
                ref="heroVideo"
                class="banner-image"
                :src="videoLoaded ? item.src : ''"
                :poster="videoPosterImage"
                playsinline
                preload="none"
                @click="toggleVideoPlay"
                @ended="handleVideoEnded"
              ></video>
              <div
                v-if="index === 0 && !videoPlaying"
                class="video-play-overlay"
                @click="toggleVideoPlay"
              >
                <svg
                  class="video-play-icon"
                  viewBox="0 0 60 60"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="30"
                    cy="30"
                    r="28"
                    fill="rgba(0,0,0,0.5)"
                    stroke="rgba(255,255,255,0.8)"
                    stroke-width="2"
                  />
                  <polygon
                    points="24,18 24,42 46,30"
                    fill="white"
                  />
                </svg>
              </div>
              <img
                v-if="index !== 0"
                class="banner-image"
                :src="item.src"
                :alt="item.alt"
                @dragstart.prevent
              >
            </div>
          </el-carousel-item>
        </el-carousel>

        <button
          class="banner-nav banner-nav-prev"
          type="button"
          aria-label="上一张"
          @click="prevSlide"
        >
          &lsaquo;
        </button>

        <button
          class="banner-nav banner-nav-next"
          type="button"
          aria-label="下一张"
          @click="nextSlide"
        >
          &rsaquo;
        </button>
      </div>
    </section>

    <section
      id="company-section"
      class="intro-section"
    >
      <div class="page-container">
        <h2>{{ introTitle }}</h2>
        <div class="course-center-line intro-title-line">
          <span></span>
        </div>
        <p
          v-for="item in introParagraphs"
          :key="item"
          class="desc-text"
        >
          {{ item }}
        </p>

        <div
          id="course-section"
          class="course-center"
        >
          <div class="course-center-head">
            <h3>{{ courseCenterTitle }}</h3>
            <div class="course-center-line">
              <span></span>
            </div>
          </div>

          <div class="tutoring-grid">
            <article
              v-for="item in courseCards"
              :key="item.key"
              class="tutoring-card"
              role="button"
              tabindex="0"
              :aria-label="`查看${item.title}课程详情`"
              @click="goCourseDetail(item.key)"
              @keydown.enter.prevent="goCourseDetail(item.key)"
              @keydown.space.prevent="goCourseDetail(item.key)"
            >
              <div class="course-circle">
                <div
                  class="course-circle-text"
                  :class="item.colorClass"
                >
                  <span
                    v-for="line in item.lines"
                    :key="line"
                  >
                    {{ line }}
                  </span>
                </div>
              </div>
              <div class="course-label">{{ item.title }}</div>
            </article>
          </div>
        </div>
      </div>
    </section>

    <section
      id="age-stage-section"
      class="age-stage-section"
    >
      <div class="page-container age-stage-inner">
        <img :src="ageImage" alt="">
      </div>
    </section>

    <section
      id="service-section"
      class="icon-service-section"
    >
      <div class="page-container">
        <div class="icon-service-grid">
          <article
            v-for="item in serviceCards"
            :key="item.title"
            class="icon-service-card"
            :class="{ 'is-clickable': item.routeName }"
            :role="item.routeName ? 'button' : null"
            :tabindex="item.routeName ? 0 : null"
            @click="handleServiceClick(item)"
            @keydown.enter.prevent="handleServiceClick(item)"
            @keydown.space.prevent="handleServiceClick(item)"
          >
            <div
              class="icon-box"
              :class="item.colorClass"
            >
              <img
                :src="item.icon"
                :alt="item.title"
              >
            </div>
            <h3>{{ item.title }}</h3>
          </article>
        </div>
      </div>
    </section>

    <section
      id="advantage-section"
      class="plan-section"
    >
      <div class="page-container">
        <div class="plan-head">
          <h2>
            {{ planTitle }}
            <span>{{ planAccent }}</span>
          </h2>
          <!-- <p>{{ planSubtitle }}</p> -->
        </div>

        <div
          class="plan-stage"
          :style="{ height: `${planStageHeight}px` }"
        >
          <div
            class="plan-fixed"
            :style="{ transform: `translateX(-50%) scale(${planScale})` }"
          >
            <div class="plan-orbit">
              <div class="orbit-ring orbit-ring-large"></div>
              <div class="orbit-ring orbit-ring-small"></div>

              <div class="orbit-center">
                <div class="orbit-center-inner">
                  <strong>{{ orbitCenterNumber }}</strong>
                  <span>{{ orbitCenterText }}</span>
                </div>
              </div>

              <article
                v-for="item in planNodes"
                :key="item.key"
                class="plan-node"
                :class="item.positionClass"
              >
                <div class="node-dot">
                  <span></span>
                </div>
                <div class="node-text">
                  <small>{{ item.system }}</small>
                  <h3>{{ item.title }}</h3>
                </div>
              </article>
            </div>

            <div class="plan-actions">
              <button
                class="plan-button"
                type="button"
              >
                {{ planButtonText }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="after-class-section">
      <div class="page-container after-class-inner">
        <img :src="afterClassImage" alt="">
      </div>
    </section>

    <section
      id="teacher-section"
      class="teacher-section"
    >
      <div class="page-container">
        <div class="teacher-head">
          <h2>{{ teacherTitle }}</h2>
          <div class="teacher-divider">
            <span></span>
          </div>
        </div>

        <div class="teacher-grid">
          <article
            v-for="item in teacherCards"
            :key="item.id || item.name"
            class="teacher-card"
            role="button"
            tabindex="0"
            @click="goTeacherDetail(item)"
            @keydown.enter.prevent="goTeacherDetail(item)"
            @keydown.space.prevent="goTeacherDetail(item)"
          >
            <div class="teacher-photo">
              <img
                v-if="item.image"
                :src="item.image"
                :alt="item.name"
              >
              <div
                v-else
                class="teacher-photo-placeholder"
              >
                {{ item.name ? item.name.slice(0, 1) : '师' }}
              </div>
            </div>
            <p class="teacher-name">{{ item.name }}</p>
            <p
              v-if="item.info"
              class="teacher-info"
            >
              {{ item.info }}
            </p>
          </article>
        </div>

        <div class="teacher-actions">
          <button
            class="teacher-more-button"
            type="button"
            @click="goTeacherList"
          >
            {{ teacherMoreText }}
          </button>
        </div>
      </div>
    </section>

    <section
      id="appointment-section"
      class="appointment-section"
    >
      <div class="page-container">
        <div class="appointment-shell">
          <!-- <div class="appointment-head">
            <h2>{{ appointmentTitle }}</h2>
            <p>{{ appointmentSubtitle }}</p>
          </div> -->

          <div class="appointment-embed">
            <iframe
              id="goldendata_form_Hl6a6R"
              class="appointment-iframe"
              src="https://jsj.top/f/Hl6a6R?background=white&banner=show&embedded=true&inner_redirect=false"
              width="100%"
              height="980"
              frameborder="0"
              scrolling="no"
              allowTransparency="true"
            ></iframe>
          </div>

          <div class="appointment-form">
            <div class="appointment-field">
              <label for="appointment-name">姓名</label>
              <el-input
                id="appointment-name"
                v-model="appointmentForm.name"
                placeholder="请输入姓名"
                clearable
              ></el-input>
            </div>

            <div class="appointment-field">
              <label for="appointment-grade">年级</label>
              <el-input
                id="appointment-grade"
                v-model="appointmentForm.grade"
                placeholder="请输入年级"
                clearable
              ></el-input>
            </div>

            <div class="appointment-field">
              <label for="appointment-phone">手机号</label>
              <el-input
                id="appointment-phone"
                v-model="appointmentForm.phone"
                placeholder="请输入手机号"
                maxlength="11"
                clearable
              ></el-input>
            </div>

            <div class="appointment-actions">
              <button
                class="appointment-button"
                type="button"
                @click="submitAppointment"
              >
                {{ appointmentButtonText }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section
      id="honor-section"
      class="honor-section"
    >
      <div class="page-container">
        <div class="honor-head">
          <h2>{{ honorTitle }}</h2>
          <div class="teacher-divider">
            <span></span>
          </div>
        </div>

        <div class="honor-carousel-wrap">
          <el-carousel
            ref="honorCarousel"
            class="honor-carousel"
            trigger="click"
            arrow="never"
            indicator-position="outside"
            :interval="4000"
          >
            <el-carousel-item
              v-for="item in honorImages"
              :key="item.src"
            >
              <div class="honor-slide">
                <img
                  class="honor-image"
                  :src="item.src"
                  :alt="item.alt"
                >
              </div>
            </el-carousel-item>
          </el-carousel>

          <button
            class="banner-nav banner-nav-prev honor-nav"
            type="button"
            aria-label="上一张荣誉图"
            @click="prevHonorSlide"
          >
            &lsaquo;
          </button>

          <button
            class="banner-nav banner-nav-next honor-nav"
            type="button"
            aria-label="下一张荣誉图"
            @click="nextHonorSlide"
          >
            &rsaquo;
          </button>
        </div>
      </div>
    </section>

    <footer class="site-footer">
      <div class="page-container site-footer-inner">
        <h2>联系方式</h2>
        <div class="site-footer-line">
          <span></span>
        </div>
        <div class="site-footer-content">
          <p>电话：4000268870</p>
          <p>邮箱：ayinuo2016@qq.com</p>
          <p>地址：山东省青岛市崂山区科苑经三路6号</p>
          <p>
            网址：
            <a
              href="https://yinuojiajiao.cn"
              target="_blank"
              rel="noopener noreferrer"
            >
              yinuojiajiao.cn
            </a>
          </p>
        </div>
      </div>
    </footer>

    <a
      class="phone-fixed-button"
      href="tel:4000268870"
      aria-label="Call 4000268870"
    >
      <img
        :src="phoneButtonImage"
        alt="Phone"
      >
      <p>联系我们</p>
    </a>
  </div>
</template>

<script>
import { extractTeacherRows, formatTeacherCard } from '../utils/teacher'
import { buildUploadAssetUrl } from '../utils/asset'
import Hls from 'hls.js'

const phoneButtonImage = require('../static/phone.png')

const sectionLinks = [
  { id: 'home-section', label: '首页' },
  { id: 'company-section', label: '企业介绍' },
  { id: 'course-section', label: '课程中心' },
  { id: 'advantage-section', label: '课程优势' },
  { id: 'teacher-section', label: '教师团队' },
  { id: 'appointment-section', label: '预约课程' },
  { id: 'honor-section', label: '企业荣誉' }
]

const swiperImagePaths = ['company.m3u8', '1.png', '2.png', '5.png']
const serviceIconPaths = {
  teacherHome: '1.png',
  onlineCourse: '2.png',
  classCourse: '3.png'
}
const honorImagePaths = [
  'honor (1).jpg',
  'honor (2).jpg',
  'honor (3).jpg',
  'honor (4).jpg',
  'honor (5).jpg',
  'honor (6).jpg',
  'honor (7).jpg',
  'honor (8).jpg',
  'honor (9).jpg',
  'honor (10).jpg',
  'honor (11).jpg',
  'honor (12).jpg',
  'honor (13).jpg'
]

const swiperImages = swiperImagePaths.map((fileName, index) => {
  const section = sectionLinks[index]
  // 视频文件在 swiper/hls 目录，图片在 swiper 目录
  const folder = index === 0 ? 'swiper/hls' : 'swiper'
  // 开发环境使用相对路径走代理，避免跨域；生产环境用绝对URL
  const src = process.env.NODE_ENV === 'development'
    ? `/upload/${folder}/${fileName}`
    : buildUploadAssetUrl(`${folder}/${fileName}`)

  return {
    src,
    alt: section ? `一诺教育${section.label}` : `一诺教育轮播图 ${index + 1}`,
    targetId: section ? section.id : ''
  }
})

const honorImages = honorImagePaths.map((fileName, index) => ({
  src: buildUploadAssetUrl(`honor/${fileName}`),
  alt: `企业荣誉 ${index + 1}`
}))

export default {
  name: 'IndexView',
  data() {
    return {
      brandName: '一诺在线教育',
      introTitle: '企业介绍',
      courseCenterTitle: '课程中心',
      ageStageTitle: '3-18岁',
      planTitle: '定制化学习方案 360°全程服务',
      planAccent: '学习更轻松',
      planSubtitle: '自主研发PPTS学生测评系统，多维度个性化学情分析',
      orbitCenterNumber: '360°',
      orbitCenterText: '教学管控',
      planButtonText: '定制专属方案',
      teacherTitle: '教师团队',
      teacherMoreText: '更多名师',
      appointmentTitle: '预约课程',
      appointmentSubtitle: '留下您的信息，我们会尽快与您联系并安排试听或咨询。',
      appointmentButtonText: '立即预约',
      honorTitle: '企业荣誉',
      navLinks: sectionLinks,
      activeSectionId: 'home-section',
      introParagraphs: [
        '一诺教育成立于2016年，深耕一对一教育领域近十年。目前以青岛为总部，并在烟台、东营、深圳设立分公司，持续拓展区域布局。',
        '公司核心特色在于打造了"机构教学与上门定制辅导相融合"的一体化教学模式，为学生提供更加灵活、高效的学习解决方案。目前青岛总部已布局七大校区，其中包含三个班课校区及四个一对一教学中心。',
        '在教学体系上，一诺教育依托多维度、严格的师资筛选机制，构建"双师融合"教学模式：由专业教师负责文化课系统讲解与成绩提升，由优质名校大学生提供课后作业辅导与学习陪伴，全面提升学生学习效果与学习体验。',
        '青岛地区：',
        '崂山九环科技大厦（班课和一对一综合校区）',
        '市南麦岛（班课和一对一综合校区）',
        '市北和达中心（一对一的教学点）',
        '李沧乐客城（一对一的教学点）',
        '崂山区北都广场（班课校区）',
        '市北凯德校区（班课校区）',
        '烟台校区地址：莱山区桐林1978',
        '深圳校区地址：福田区莲花街道福中社区',
        '东营校区地址：东营东城银座'
      ],
      swiperImages,
      honorImages,
      phoneButtonImage,
      videoPosterImage: buildUploadAssetUrl('swiper/cover.png'),
      videoPlaying: false,
      videoLoaded: false,
      videoSrc: swiperImages[0]?.src || '',
      hlsInstance: null,
      lazyLoadObserver: null,
      ageImage: buildUploadAssetUrl(`swiper/ageimg.png`),
      afterClassImage: buildUploadAssetUrl(`swiper/after_class.png`),
      touchStartX: 0,
      touchCurrentX: 0,
      pointerStartX: 0,
      pointerCurrentX: 0,
      isPointerDown: false,
      swipeThreshold: 40,
      suppressBannerClick: false,
      heroCarouselTimer: null,
      heroCarouselActiveIndex: 0,
      appointmentForm: {
        name: '',
        grade: '',
        phone: ''
      },
      windowWidth: 0,
      planBaseWidth: 980,
      planBaseHeight: 850,
      courseCards: [
        { key: 'small', title: '小学', lines: ['小学'], colorClass: 'tone-purple' },
        { key: 'middle12', title: '初一/初二', lines: ['初一', '初二'], colorClass: 'tone-green' },
        { key: 'middle3', title: '初三', lines: ['初三'], colorClass: 'tone-red' },
        { key: 'heigh12', title: '高一/高二', lines: ['高一', '高二'], colorClass: 'tone-orange' },
        { key: 'heigh3', title: '高三', lines: ['高三'], colorClass: 'tone-blue' },
        { key: 'yikao', title: '艺考', lines: ['艺考'], colorClass: 'tone-pink' }
      ],
      ageStageCards: [
        {
          index: '01',
          title: '校内课',
          points: [
            { text: '校内全科提分', color: 'bar-pink' },
            { text: '作业陪读', color: 'bar-cyan' },
            { text: '单词速记', color: 'bar-pink' }
          ]
        },
        {
          index: '02',
          title: '拓展课',
          points: [
            { text: '剑桥少儿英语 / 口语', color: 'bar-pink' },
            { text: '奥数 / 举一反三', color: 'bar-cyan' },
            { text: '大语文', color: 'bar-pink' }
          ]
        },
        {
          index: '03',
          title: '特色课',
          points: [
            { text: '体适能 / 中高考体育', color: 'bar-pink' },
            { text: 'AP / A-Level 国际课', color: 'bar-cyan' },
            { text: '幼小衔接 / 启蒙课', color: 'bar-pink' }
          ]
        }
      ],
      serviceCards: [
        {
          title: '名师到家',
          icon: buildUploadAssetUrl(`icon/${serviceIconPaths.teacherHome}`),
          colorClass: 'service-orange',
          routeName: 'teacher-home'
        },
        {
          title: '在线课程',
          icon: buildUploadAssetUrl(`icon/${serviceIconPaths.onlineCourse}`),
          colorClass: 'service-green',
          routeName: 'online-course'
        },
        {
          title: '班课课程',
          icon: buildUploadAssetUrl(`icon/${serviceIconPaths.classCourse}`),
          colorClass: 'service-blue',
          routeName: 'class-course'
        }
      ],
      planNodes: [
        {
          key: 'analysis',
          system: '测评系统',
          title: '学员学情分析',
          positionClass: 'node-top'
        },
        {
          key: 'teacher-match',
          system: '教学管理系统',
          title: '匹配合适老师',
          positionClass: 'node-upper-right'
        },
        {
          key: 'meeting',
          system: '教学系统',
          title: '学情沟通会',
          positionClass: 'node-middle-right'
        },
        {
          key: 'trial',
          system: '教学系统',
          title: '试听课',
          positionClass: 'node-lower-right'
        },
        {
          key: 'plan',
          system: '教研资源系统 / 教学资源系统 / 备课系统',
          title: '个性化课程规划 / 个性化备课',
          positionClass: 'node-bottom'
        },
        {
          key: 'one-to-one',
          system: '教学系统 / 教学资源系统 / 备课系统',
          title: '老师 1 对 1 授课',
          positionClass: 'node-lower-left'
        },
        {
          key: 'tracking',
          system: '教学管理系统',
          title: '学管老师跟踪、反馈、调整',
          positionClass: 'node-middle-left'
        },
        {
          key: 'quality',
          system: '教学管理系统 / CRM',
          title: '质检老师监管授课质量 / 学员回访',
          positionClass: 'node-upper-left'
        }
      ],
      teacherCards: []
    }
  },
  computed: {
    planScale() {
      if (!this.windowWidth) {
        return 1
      }

      const availableWidth = Math.max(this.windowWidth - 32, 320)
      return Math.min(1, availableWidth / this.planBaseWidth)
    },
    planStageHeight() {
      return Math.round(this.planBaseHeight * this.planScale)
    }
  },
  mounted() {
    document.title = '一诺教育'
    this.handleResize()
    this.fetchTeacherList()
    window.addEventListener('resize', this.handleResize)
    // 延迟2秒后加载视频，避免阻塞其他资源
    setTimeout(() => {
      this.loadHeroVideo()
    }, 2000)
    this.$nextTick(() => {
      this.scrollRouteHash()
      this.startHeroCarouselTimer()
    })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    this.clearHeroCarouselTimer()
    if (this.hlsInstance) {
      this.hlsInstance.destroy()
      this.hlsInstance = null
    }
    if (this.lazyLoadObserver) {
      this.lazyLoadObserver.disconnect()
      this.lazyLoadObserver = null
    }
  },
  methods: {
    async fetchTeacherList() {
      try {
        const response = await this.$get('/teacher/listPage', {
          current: 1,
          size: 6
        })
        const rows = extractTeacherRows(response)

        this.teacherCards = rows.slice(0, 6).map(formatTeacherCard)
      } catch (error) {
        this.teacherCards = []
      }
    },
    handleResize() {
      this.windowWidth = window.innerWidth
    },
    goCourseDetail(type) {
      this.$router.push({
        name: 'course-detail',
        params: {
          type
        }
      })
    },
    goTeacherList() {
      this.$router.push({ name: 'teacher-list' })
    },
    goTeacherDetail(item) {
      if (!item || !item.id) {
        return
      }

      this.$router.push({
        name: 'teacher-detail',
        params: {
          id: item.id
        }
      })
    },
    handleServiceClick(item) {
      if (!item.routeName) {
        return
      }

      this.$router.push({ name: item.routeName })
    },
    scrollRouteHash() {
      if (!this.$route.hash) {
        return
      }

      this.scrollToSection(this.$route.hash.replace('#', ''))
    },
    handleNav(id) {
      this.activeSectionId = id

      if (id === 'home-section') {
        this.scrollToTop()
        return
      }

      this.scrollToSection(id)
    },
    handleBannerClick(item) {
      if (this.suppressBannerClick || !item.targetId) {
        return
      }

      this.handleNav(item.targetId)
    },
    handleVideoError(event, index) {
      const video = event.target
      const fallbackImage = this.buildFallbackImage(index)
      if (fallbackImage && video.parentElement) {
        const img = document.createElement('img')
        img.src = fallbackImage
        img.className = 'banner-image'
        img.alt = `一诺教育轮播图 ${index + 1}`
        video.parentElement.replaceChild(img, video)
      }
    },
    buildFallbackImage(index) {
      return buildUploadAssetUrl(`swiper/fallback_${index}.png`)
    },
    toggleVideoPlay(event) {
      const videoRef = this.$refs.heroVideo
      const video = Array.isArray(videoRef) ? videoRef[0] : videoRef
      if (!video) {
        return
      }
      event.stopPropagation()

      // 如果视频还没加载，先加载再播放
      if (!this.videoLoaded) {
        this.videoLoaded = true

        // 使用 HLS.js 加载视频
        if (Hls.isSupported()) {
          this.hlsInstance = new Hls({
            enableWorker: true,
            lowLatencyMode: true
          })
          this.hlsInstance.loadSource(this.videoSrc)
          this.hlsInstance.attachMedia(video)
          this.hlsInstance.on(Hls.Events.MANIFEST_PARSED, () => {
            video.play().catch(() => {})
            this.videoPlaying = true
          })
          this.hlsInstance.on(Hls.Events.ERROR, (event, data) => {
            if (data.fatal) {
              console.error('HLS error:', data)
            }
          })
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
          // iOS Safari原生支持HLS
          video.src = this.videoSrc
          video.addEventListener('loadedmetadata', () => {
            video.play().catch(() => {})
            this.videoPlaying = true
          })
        }
        return
      }

      if (video.paused) {
        video.play().catch(() => {})
        this.videoPlaying = true
      } else {
        video.pause()
        this.videoPlaying = false
      }
    },
    handleVideoEnded() {
      this.videoPlaying = false
      this.nextSlide()
    },
    loadHeroVideo() {
      const videoRef = this.$refs.heroVideo
      const video = Array.isArray(videoRef) ? videoRef[0] : videoRef
      if (video && video.src && !video.src.includes('blob:')) {
        video.load()
      }
    },
    clearHeroCarouselTimer() {
      if (!this.heroCarouselTimer) {
        return
      }

      window.clearTimeout(this.heroCarouselTimer)
      this.heroCarouselTimer = null
    },
    startHeroCarouselTimer() {
      this.clearHeroCarouselTimer()

      if (!this.$refs.heroCarousel || this.swiperImages.length <= 1) {
        return
      }

      const duration = this.heroCarouselActiveIndex === 0 ? 10000 : 5000
      this.heroCarouselTimer = window.setTimeout(() => {
        this.nextSlide()
      }, duration)
    },
    handleHeroCarouselChange(index) {
      // 当离开视频轮播项时，暂停视频
      if (this.heroCarouselActiveIndex === 0 && index !== 0) {
        const videoRef = this.$refs.heroVideo
        const video = Array.isArray(videoRef) ? videoRef[0] : videoRef
        if (video && !video.paused) {
          video.pause()
          this.videoPlaying = false
        }
      }
      // 移动端自动播放视频
      if (index === 0 && 'ontouchstart' in window && !this.videoPlaying) {
        this.$nextTick(() => {
          this.toggleVideoPlay({ stopPropagation: () => {} })
        })
      }
      this.heroCarouselActiveIndex = index
      this.startHeroCarouselTimer()
    },
    prevSlide() {
      if (this.$refs.heroCarousel) {
        this.$refs.heroCarousel.prev()
      }
    },
    nextSlide() {
      if (this.$refs.heroCarousel) {
        this.$refs.heroCarousel.next()
      }
    },
    prevHonorSlide() {
      if (this.$refs.honorCarousel) {
        this.$refs.honorCarousel.prev()
      }
    },
    nextHonorSlide() {
      if (this.$refs.honorCarousel) {
        this.$refs.honorCarousel.next()
      }
    },
    submitAppointment() {
      const { name, grade, phone } = this.appointmentForm

      if (!name || !grade || !phone) {
        this.$message.warning('请完整填写姓名、年级和手机号')
        return
      }

      this.$message.success('预约信息已提交，我们会尽快与您联系')
      this.appointmentForm = {
        name: '',
        grade: '',
        phone: ''
      }
    },
    handleTouchStart(event) {
      this.touchStartX = event.changedTouches[0].clientX
      this.touchCurrentX = this.touchStartX
    },
    handleTouchMove(event) {
      this.touchCurrentX = event.changedTouches[0].clientX
    },
    handleTouchEnd() {
      this.handleSwipe(this.touchCurrentX - this.touchStartX)
      this.touchStartX = 0
      this.touchCurrentX = 0
    },
    handlePointerStart(event) {
      this.isPointerDown = true
      this.pointerStartX = event.clientX
      this.pointerCurrentX = event.clientX
    },
    handlePointerMove(event) {
      if (!this.isPointerDown) {
        return
      }

      this.pointerCurrentX = event.clientX
    },
    handlePointerEnd() {
      if (!this.isPointerDown) {
        return
      }

      this.isPointerDown = false
      this.handleSwipe(this.pointerCurrentX - this.pointerStartX)
      this.pointerStartX = 0
      this.pointerCurrentX = 0
    },
    handlePointerLeave() {
      if (!this.isPointerDown) {
        return
      }

      this.isPointerDown = false
      this.pointerStartX = 0
      this.pointerCurrentX = 0
    },
    handleSwipe(deltaX) {
      if (Math.abs(deltaX) < this.swipeThreshold) {
        return
      }

      this.suppressBannerClick = true
      window.setTimeout(() => {
        this.suppressBannerClick = false
      }, 250)

      if (deltaX < 0) {
        this.nextSlide()
        return
      }

      this.prevSlide()
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    scrollToSection(id) {
      const target = document.getElementById(id)

      if (target) {
        const navbar = this.$el.querySelector('.navbar')
        const offset = navbar ? navbar.offsetHeight : 0
        const top = target.getBoundingClientRect().top + window.pageYOffset - offset

        window.scrollTo({
          top: Math.max(top, 0),
          behavior: 'smooth'
        })
      }
    }
  }
}
</script>

<style scoped>
.education-home {
  padding-bottom: 64px;
}

.navbar {
  position: sticky;
  top: 0;
  z-index: 10;
  background: rgba(255, 255, 255, 0.96);
  border-bottom: 1px solid #eee;
  backdrop-filter: blur(10px);
}

.navbar-inner {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 68px;
  gap: 20px;
}

.navbar-left {
  display: flex;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;
}

.navbar-left a {
  position: relative;
  padding: 8px 0;
  color: #333;
  font-size: 13px;
  transition: color 0.2s ease;
}

.navbar-left a.is-active {
  color: #ff7d29;
  font-weight: 600;
}

.navbar-left a.is-active::after {
  content: '';
  position: absolute;
  right: 0;
  bottom: 2px;
  left: 0;
  height: 2px;
  border-radius: 999px;
  background: #ff7d29;
}

.navbar-left .brand {
  color: #ff7d29;
  font-weight: 700;
}

.hero-section {
  width: 100%;
}

.banner-wrap {
  position: relative;
  width: 100%;
  user-select: none;
}

.banner {
  width: 100%;
  background: #f5f5f5;
}

.banner-slide {
  position: relative;
  width: 100%;
  height: 100%;
  cursor: pointer;
  outline: none;
  display: flex;
  align-items: center;
  justify-content: center;
}

.banner-slide:focus-visible {
  box-shadow: inset 0 0 0 3px rgba(255, 125, 41, 0.72);
}

.banner-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  margin-left: auto;
  margin-right: auto;
  display: block;
}

video.banner-image {
  background: transparent;
  border: none;
}

.video-play-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;
}

.video-play-icon {
  width: 80px;
  height: 80px;
  transition: transform 0.2s ease;
}

.video-play-overlay:hover .video-play-icon {
  transform: scale(1.1);
}

.banner-nav {
  position: absolute;
  top: 50%;
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.82);
  color: #666;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  transform: translateY(-50%);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  transition: background-color 0.2s ease, color 0.2s ease;
      padding-bottom: 5px;
      box-sizing: border-box;
}

.banner-nav:hover {
  color: #ff7d29;
  background: #fff;
}

.banner-nav-prev {
  left: 16px;
}

.banner-nav-next {
  right: 16px;
}

.hero-section,
.intro-section,
.course-center,
.plan-section,
.teacher-section,
.appointment-section,
.honor-section {
  width: 100%;
  scroll-margin-top: 76px;
}

.intro-section {
  padding: 60px 0;
  text-align: center;
}

.intro-section h2 {
  font-size: 46px;
  font-weight: 500;
  color: #333;
  letter-spacing: 2px;
}

.intro-title-line {
  margin-bottom: 40px;
}

.desc-text {
  max-width: 900px;
  margin: 0 auto 20px;
  padding: 0 10px;
  font-size: 18px;
  line-height: 2;
  color: #666;
  text-align: left;
  text-indent: 2em;
}

.course-center {
  margin-top: 56px;
}

.course-center-head h3 {
  font-size: 46px;
  font-weight: 500;
  color: #333;
  letter-spacing: 2px;
}

.course-center-line {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 28px auto 40px;
}

.course-center-line::before,
.course-center-line::after {
  content: '';
  width: 140px;
  height: 4px;
  border-radius: 999px;
  background: linear-gradient(90deg, #013F7D 0%, #012a56 100%);
}

.course-center-line span {
  width: 18px;
  height: 18px;
  margin: 0 14px;
  background: linear-gradient(135deg, #013F7D 0%, #012a56 100%);
  box-shadow: 0 0 0 4px rgba(1, 63, 125, 0.12);
  transform: rotate(45deg);
}

.tutoring-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 40px 28px;
  justify-items: center;
  margin-top: 8px;
  padding: 0 12px;
}

.tutoring-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  outline: none;
}

.tutoring-card:focus-visible .course-circle {
  box-shadow:
    inset 0 10px 28px rgba(255, 255, 255, 0.92),
    inset 0 -12px 26px rgba(0, 0, 0, 0.08),
    0 0 0 4px rgba(255, 125, 41, 0.2),
    0 10px 22px rgba(0, 0, 0, 0.14);
}

.course-circle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: clamp(180px, 20vw, 260px);
  height: clamp(180px, 20vw, 260px);
  border-radius: 50%;
  background: radial-gradient(circle at 35% 28%, #ffffff 0%, #fbfbfb 36%, #ececec 70%, #d6d6d6 100%);
  box-shadow:
    inset 0 10px 28px rgba(255, 255, 255, 0.92),
    inset 0 -12px 26px rgba(0, 0, 0, 0.08),
    0 10px 22px rgba(0, 0, 0, 0.14);
}

.course-circle-text {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: clamp(2.4rem, 4.3vw, 4.5rem);
  font-weight: 700;
  line-height: 0.95;
  letter-spacing: 2px;
  text-shadow: 0 2px 8px rgba(255, 255, 255, 0.85);
}

.course-circle-text span + span {
  margin-top: 10px;
}

.course-label {
  margin-top: 24px;
  font-size: clamp(1.8rem, 2.4vw, 3rem);
  line-height: 1.2;
  letter-spacing: 1px;
  color: #666;
}

.tone-purple {
  color: #9f7cf2;
}

.tone-green {
  color: #013F7D;
}

.tone-red {
  color: #ef7f83;
}

.tone-orange {
  color: #e69c6f;
}

.tone-blue {
  color: #6f91f1;
}

.tone-pink {
  color: #e87ab0;
}

.age-stage-section {
  width: 100%;
  padding: 14px 0 12px;
  box-sizing: border-box;
  /* background: #4f8f35; */
}

.age-stage-inner {
  position: relative;
  width: 100%;
}
.age-stage-inner img{
  width: 100%;
  height: auto;
}

.age-stage-head {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 34px;
}

.age-stage-side {
  display: flex;
  flex: 0 1 auto;
  align-items: center;
}

.age-stage-side-right {
  justify-content: flex-start;
}

.age-stage-title {
  flex: 0 0 auto;
  display: flex;
  justify-content: center;
}

.age-stage-title h2 {
  font-size: clamp(2.75rem, 7vw, 5.5rem);
  font-weight: 800;
  line-height: 1;
  color: #fff;
  letter-spacing: 1px;
}

.dot-cluster {
  display: grid;
  grid-template-columns: repeat(6, 8px);
  gap: 8px 10px;
}

.dot-cluster span {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.88);
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.1);
}

.age-stage-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;
}

.age-stage-card {
  position: relative;
  min-height: 210px;
  padding: 28px 28px 24px;
  overflow: hidden;
  border-radius: 2px;
  background: #ffe35a;
  box-shadow: 0 10px 18px rgba(40, 77, 24, 0.18);
}

.age-stage-index {
  position: absolute;
  top: -10px;
  left: 20px;
  font-size: 4.5rem;
  font-weight: 800;
  line-height: 1;
  color: rgba(255, 255, 255, 0.45);
}

.age-stage-card h3 {
  position: relative;
  z-index: 1;
  margin-bottom: 18px;
  font-size: 2rem;
  font-weight: 800;
  line-height: 1.1;
  color: #013F7D;
}

.age-stage-card ul {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  list-style: none;
}

.age-stage-card li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 1rem;
  line-height: 1.35;
  color: #013F7D;
}

.point-bar {
  flex-shrink: 0;
  width: 4px;
  height: 28px;
  margin-top: 1px;
  border-radius: 999px;
}

.bar-pink {
  background: #ef8db8;
}

.bar-cyan {
  background: #73d4d7;
}

.icon-service-section {
  padding: 64px 0 48px;
  background: #fff;
}

.icon-service-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 28px;
  align-items: start;
}

.icon-service-card {
  text-align: center;
  outline: none;
}

.icon-service-card.is-clickable {
  cursor: pointer;
}

.icon-service-card.is-clickable:focus-visible .icon-box {
  box-shadow:
    0 0 0 4px rgba(255, 125, 41, 0.18),
    0 16px 30px rgba(15, 23, 42, 0.08);
}

.icon-box {
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1 / 1;
  border-radius: 30px;
  box-shadow: 0 16px 30px rgba(15, 23, 42, 0.08);
}

.icon-box img {
  width: 44%;
  max-width: 116px;
}

.service-orange {
  background: linear-gradient(180deg, #ffac12 0%, #ff9800 100%);
}

.service-green {
  background: linear-gradient(180deg, #20b676 0%, #17ab6d 100%);
}

.service-blue {
  background: linear-gradient(180deg, #54b8f1 0%, #46a9ea 100%);
}

.icon-service-card h3 {
  margin-top: 18px;
  font-size: clamp(1.8rem, 2.6vw, 3rem);
  font-weight: 500;
  line-height: 1.2;
  color: #4a4a4a;
  letter-spacing: 1px;
}

.plan-section {
  padding: 26px 0 82px;
  overflow: hidden;
  background:
    radial-gradient(circle at center, rgba(255, 94, 97, 0.04), transparent 38%),
    linear-gradient(180deg, #ffffff 0%, #fffdfd 100%);
}

.plan-head {
  text-align: center;
}

.plan-head h2 {
  font-size: clamp(2rem, 3.4vw, 3.2rem);
  font-weight: 400;
  line-height: 1.35;
  color: #28313d;
}

.plan-head h2 span {
  font-weight: 700;
  color: #1f2d3d;
}

.plan-head p {
  margin-top: 18px;
  font-size: 0.95rem;
  color: #aeb4bb;
}

.plan-stage {
  position: relative;
  width: 100%;
  margin-top: 44px;
}

.plan-fixed {
  position: absolute;
  top: 0;
  left: 50%;
  width: 980px;
  height: 850px;
  transform-origin: top center;
}

.plan-orbit {
  position: relative;
  width: 980px;
  height: 760px;
  margin: 0 auto;
}

.orbit-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.orbit-ring-large {
  width: 610px;
  height: 610px;
  border: 5px dotted rgba(255, 118, 121, 0.22);
  animation: orbit-spin-large 26s linear infinite;
}

.orbit-ring-small {
  width: 470px;
  height: 470px;
  border: 9px dotted rgba(255, 118, 121, 0.16);
  animation: orbit-spin-small 18s linear infinite;
}

.orbit-center {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 242px;
  height: 242px;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  background: radial-gradient(circle at 34% 28%, #ff666c 0%, #ff5057 44%, #ff3a3e 100%);
  box-shadow:
    0 0 0 12px rgba(255, 95, 99, 0.05),
    0 18px 34px rgba(255, 82, 87, 0.26);
}

.orbit-center-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #fff;
  text-align: center;
}

.orbit-center-inner strong {
  font-size: 4.2rem;
  line-height: 1;
  font-weight: 800;
}

.orbit-center-inner span {
  margin-top: 12px;
  font-size: 1.7rem;
  font-weight: 500;
}

.plan-node {
  position: absolute;
  display: flex;
  align-items: center;
  gap: 14px;
  max-width: 280px;
}

.node-dot {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  border-radius: 50%;
  background: rgba(255, 84, 92, 0.18);
}

.node-dot span {
  width: 13px;
  height: 13px;
  border-radius: 50%;
  background: #ff4d55;
}

.node-text small {
  display: block;
  margin-bottom: 8px;
  font-size: 1.08rem;
  color: #b4b8be;
}

.node-text h3 {
  font-size: 1.22rem;
  line-height: 1.6;
  font-weight: 500;
  color: #333940;
}

.node-top {
  top: 4px;
  left: 50%;
  flex-direction: column;
  max-width: 220px;
  text-align: center;
  transform: translateX(-50%);
}

.node-upper-right {
  top: 108px;
  right: 86px;
}

.node-middle-right {
  top: 302px;
  right: 58px;
}

.node-lower-right {
  right: 86px;
  bottom: 116px;
}

.node-bottom {
  bottom: 0;
  left: 50%;
  flex-direction: column;
  max-width: 360px;
  text-align: center;
  transform: translateX(-50%);
}

.node-lower-left {
  bottom: 116px;
  left: 34px;
  flex-direction: row-reverse;
  text-align: right;
}

.node-middle-left {
  top: 302px;
  left: 8px;
  flex-direction: row-reverse;
  text-align: right;
}

.node-upper-left {
  top: 108px;
  left: 34px;
  flex-direction: row-reverse;
  text-align: right;
}

.plan-actions {
  display: flex;
  justify-content: center;
  margin-top: 30px;
}

.plan-button {
  min-width: 282px;
  padding: 15px 28px;
  border: none;
  border-radius: 999px;
  background: linear-gradient(180deg, #ff4b4e 0%, #ff3437 100%);
  color: #fff;
  font-size: 1.45rem;
  cursor: pointer;
  box-shadow: 0 14px 28px rgba(255, 70, 73, 0.22);
}

.after-class-section {
  padding: 14px 0 12px;
}

.after-class-inner {
  position: relative;
  width: 100%;
}

.after-class-inner img {
  width: 100%;
  height: auto;
}

.teacher-section {
  padding: 32px 0 56px;
  background: #fff;
}

.teacher-head,
.honor-head {
  text-align: center;
}

.teacher-head h2,
.honor-head h2 {
  font-size: 2rem;
  font-weight: 500;
  color: #4f4f4f;
}

.teacher-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 18px auto 34px;
}

.teacher-divider::before,
.teacher-divider::after {
  content: '';
  width: 92px;
  height: 2px;
  background: #013F7D;
}

.teacher-divider span {
  position: relative;
  width: 12px;
  height: 12px;
  margin: 0 12px;
  border-radius: 50%;
  background: #013F7D;
}

.teacher-divider span::before,
.teacher-divider span::after {
  content: '';
  position: absolute;
  top: 50%;
  width: 4px;
  height: 4px;
  margin-top: -2px;
  border-radius: 50%;
  background: #94c5e8;
}

.teacher-divider span::before {
  left: -10px;
}

.teacher-divider span::after {
  right: -10px;
}

.teacher-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 24px 18px;
}

.teacher-card {
  flex: 1 1 calc(50% - 9px);
  min-width: calc(50% - 9px);
  max-width: calc(50% - 9px);
  text-align: center;
  cursor: pointer;
}

.teacher-photo {
  position: relative;
  /* aspect-ratio: 1 / 1; */
  overflow: hidden;
  background: #e5e7f0;
}

.teacher-photo img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.teacher-photo-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #fff4e8 0%, #ffe0c0 100%);
  color: #ff7d29;
  font-size: 3rem;
  font-weight: 700;
}

.teacher-name {
  margin-top: 12px;
  font-size: 1rem;
  line-height: 1.5;
  color: #737373;
}

.teacher-info {
  display: -webkit-box;
  margin-top: 6px;
  overflow: hidden;
  color: #9aa0a8;
  font-size: 0.9rem;
  line-height: 1.6;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.teacher-actions {
  display: flex;
  justify-content: center;
  margin-top: 30px;
}

.teacher-more-button {
  min-width: 168px;
  padding: 12px 28px;
  border: 1px solid #013F7D;
  border-radius: 999px;
  background: #fff;
  color: #013F7D;
  font-size: 1rem;
  line-height: 1.2;
  cursor: pointer;
  transition: all 0.2s ease;
}

.teacher-more-button:hover {
  background: #013F7D;
  color: #fff;
  box-shadow: 0 10px 22px rgba(1, 63, 125, 0.24);
}

.appointment-section {
  padding: 12px 0 56px;
  background: linear-gradient(180deg, #ffffff 0%, #fbfcff 100%);
}

.appointment-shell {
  padding: 34px 30px 30px;
  border-radius: 28px;
  background: #fff;
  box-shadow: 0 16px 38px rgba(107, 137, 173, 0.14);
}

.appointment-head {
  text-align: center;
}

.appointment-head h2 {
  font-size: 2rem;
  font-weight: 600;
  color: #3b3b3b;
}

.appointment-head p {
  margin-top: 12px;
  font-size: 1rem;
  line-height: 1.8;
  color: #7c8798;
}

.appointment-embed {
  margin-top: 28px;
  width: 100%;
  overflow: hidden;
  border-radius: 20px;
}

.appointment-iframe {
  display: block;
  width: 100%;
  max-width: 100%;
  height: 980px;
  border: 0;
  background: #fff;
  overflow: hidden;
}

.appointment-form {
  display: none;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 28px;
}

.appointment-field {
  flex: 1 1 220px;
  min-width: 220px;
}

.appointment-field label {
  display: block;
  margin-bottom: 10px;
  font-size: 1rem;
  font-weight: 600;
  color: #4e5561;
}

.appointment-actions {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  flex: 1 1 200px;
  min-width: 200px;
}

.appointment-button {
  min-width: 180px;
  padding: 13px 26px;
  border: none;
  border-radius: 999px;
  background: linear-gradient(180deg, #ff9f18 0%, #ff8600 100%);
  color: #fff;
  font-size: 1rem;
  cursor: pointer;
  box-shadow: 0 14px 28px rgba(255, 142, 25, 0.24);
}

.honor-section {
  padding: 4px 0 72px;
  background: #fff;
}

.site-footer {
  padding: 40px 0 56px;
  background: linear-gradient(180deg, #f7fbf4 0%, #eef7e9 100%);
  border-top: 1px solid rgba(126, 203, 102, 0.18);
}

.site-footer-inner {
  text-align: center;
}

.site-footer-inner h2 {
  font-size: 2rem;
  font-weight: 500;
  color: #4f4f4f;
}

.site-footer-line {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 18px auto 28px;
}

.site-footer-line::before,
.site-footer-line::after {
  content: '';
  width: 92px;
  height: 2px;
  background: #013F7D;
}

.site-footer-line span {
  position: relative;
  width: 12px;
  height: 12px;
  margin: 0 12px;
  border-radius: 50%;
  background: #013F7D;
}

.site-footer-line span::before,
.site-footer-line span::after {
  content: '';
  position: absolute;
  top: 50%;
  width: 4px;
  height: 4px;
  margin-top: -2px;
  border-radius: 50%;
  background: #94c5e8;
}

.site-footer-line span::before {
  left: -10px;
}

.site-footer-line span::after {
  right: -10px;
}

.site-footer-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  color: #5f6b5f;
  font-size: 1rem;
  line-height: 1.9;
}

.site-footer-content a {
  color: #013F7D;
  text-decoration: none;
}

.site-footer-content a:hover {
  text-decoration: underline;
}

.honor-carousel-wrap {
  position: relative;
  margin-top: 18px;
}

.honor-carousel {
  width: 100%;
}

.honor-carousel ::v-deep .el-carousel__container {
  width: 100%;
  height: clamp(420px, 72vw, 980px);
}

.honor-carousel ::v-deep .el-carousel__item {
  height: 100%;
}

.honor-slide {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.honor-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
}

.honor-nav {
  top: 50%;
}

.phone-fixed-button {
  position: fixed;
  right: 14px;
  top: 50%;
  z-index: 120;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  width: 100px;
  height: 100px;
  border-radius: 50%;
}

.phone-fixed-button img {
  display: block;
 width: 50px;
  height: 50px;
  object-fit: contain;
  margin:0 auto;
}

@keyframes orbit-spin-large {
  from {
    transform: translate(-50%, -50%) rotate(0deg);
  }

  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

@keyframes orbit-spin-small {
  from {
    transform: translate(-50%, -50%) rotate(360deg);
  }

  to {
    transform: translate(-50%, -50%) rotate(0deg);
  }
}

.banner ::v-deep .el-carousel__container {
  width: 100%;
  height: 50vh;
  background: #f5f5f5;
}

.banner ::v-deep .el-carousel__item {
  width: 100%;
  height: 100%;
}

.banner ::v-deep .el-carousel__item > div {
  width: 100%;
  height: 100%;
}

::v-deep .el-carousel__indicators--outside {
  position: absolute;
  bottom: 20px;
  left: 50%;
  z-index: 3;
  display: flex;
  gap: 10px;
  margin: 0;
  transform: translateX(-50%);
}

::v-deep .el-carousel__indicator--horizontal {
  padding: 0;
}

::v-deep .el-carousel__button {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  opacity: 1;
  background: rgba(255, 255, 255, 0.5);
}

::v-deep .el-carousel__indicator.is-active .el-carousel__button {
  background: #ff7d29;
}

@media (min-width: 960px) {
  .teacher-card {
    flex-basis: calc(33.3333% - 12px);
    min-width: calc(33.3333% - 12px);
    max-width: calc(33.3333% - 12px);
  }
}

@media (min-width: 1360px) {
  .teacher-card {
    flex-basis: calc(25% - 14px);
    min-width: calc(25% - 14px);
    max-width: calc(25% - 14px);
  }
}

@media (max-width: 768px) {
  .education-home {
    padding-bottom: 48px;
  }

  .navbar-inner {
    min-height: auto;
    padding-top: 10px;
    padding-bottom: 10px;
  }

  .navbar-left {
    gap: 8px;
  }

  .navbar-left a {
    font-size: 11px;
  }

  .banner-nav {
    width: 36px;
    height: 36px;
    font-size: 24px;
  }

  .banner-nav-prev {
    left: 10px;
  }

  .banner-nav-next {
    right: 10px;
  }

  .banner ::v-deep .el-carousel__container {
    height: auto !important;
    min-height: 200px;
  }

  .banner ::v-deep .el-carousel__indicators--outside {
    display: none !important;
  }

  .banner-slide {
    height: auto !important;
    min-height: 200px;
  }

  .banner-image {
    max-height: none !important;
    height: auto !important;
  }

  .intro-section {
    padding: 40px 0;
  }

  .intro-section h2 {
    font-size: 30px;
  }

  .desc-text {
    font-size: 15px;
    line-height: 1.9;
  }

  .course-center {
    margin-top: 44px;
  }

  .course-center-head h3 {
    font-size: 30px;
  }

  .course-center-line {
    margin: 20px auto 30px;
  }

  .course-center-line::before,
  .course-center-line::after {
    width: 72px;
    height: 3px;
  }

  .course-center-line span {
    width: 12px;
    height: 12px;
    margin: 0 10px;
  }

  .tutoring-grid {
    gap: 26px 12px;
    padding: 0 4px;
  }

  .course-circle {
    width: 104px;
    height: 104px;
  }

  .course-circle-text {
    font-size: 2rem;
    letter-spacing: 0;
  }

  .course-circle-text span + span {
    margin-top: 6px;
  }

  .course-label {
    margin-top: 14px;
    font-size: 1rem;
  }

  .age-stage-section {
    width: 100%;
    height: auto;
    padding: 14px 0 12px;
  box-sizing: border-box;

  }

  .age-stage-head {
    gap: 10px;
    margin-bottom: 24px;
  }

  .age-stage-side {
    flex: 0 1 auto;
  }

  .age-stage-title h2 {
    font-size: 2.4rem;
    letter-spacing: 0;
  }

  .dot-cluster {
    grid-template-columns: repeat(6, 7px);
    gap: 7px 8px;
  }

  .dot-cluster span {
    width: 6px;
    height: 6px;
  }

  .age-stage-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 6px;
  }

  .age-stage-card {
    min-height: auto;
    padding: 14px 10px 10px;
  }

  .age-stage-index {
    top: -4px;
    left: 14px;
    font-size: 3.25rem;
  }

  .age-stage-card h3 {
    font-size: 1.2rem;
  }

  .age-stage-card li {
    font-size: 0.8rem;
  }

  .point-bar {
    height: 24px;
  }

  .icon-service-section {
    padding: 14px 0 12px;
  }

  .icon-service-grid {
    gap: 12px;
  }

  .icon-box {
    border-radius: 18px;
  }

  .icon-service-card h3 {
    margin-top: 12px;
    font-size: 1rem;
  }

  .teacher-section {
    padding: 26px 0 40px;
  }

  .teacher-head h2,
  .honor-head h2,
  .appointment-head h2 {
    font-size: 1.55rem;
  }

  .teacher-divider {
    margin: 14px auto 24px;
  }

  .teacher-divider::before,
  .teacher-divider::after {
    width: 64px;
  }

  .teacher-grid {
    gap: 16px 12px;
  }

  .teacher-card {
    flex-basis: calc(50% - 6px);
    min-width: calc(50% - 6px);
    max-width: calc(50% - 6px);
  }

  .teacher-name {
    margin-top: 8px;
    font-size: 0.92rem;
  }

  .teacher-actions {
    margin-top: 22px;
  }

  .teacher-more-button {
    min-width: 138px;
    padding: 10px 22px;
    font-size: 0.92rem;
  }

  .appointment-section {
    padding: 8px 0 40px;
  }

  .appointment-shell {
    padding: 24px 16px 20px;
    border-radius: 20px;
  }

  .appointment-head p {
    font-size: 0.92rem;
  }

  .appointment-embed {
    margin-top: 22px;
  }

  .appointment-iframe {
    height: 860px;
  }

  .appointment-form {
    gap: 14px;
    margin-top: 22px;
  }

  .appointment-field,
  .appointment-actions {
    flex-basis: 100%;
    min-width: 100%;
  }

  .appointment-field label {
    margin-bottom: 8px;
    font-size: 0.95rem;
  }

  .appointment-button {
    width: 100%;
    min-width: 0;
  }

  .honor-section {
    padding: 0 0 48px;
  }

  .site-footer {
    padding: 28px 0 36px;
  }

  .site-footer-inner h2 {
    font-size: 1.55rem;
  }

  .site-footer-line {
    margin: 14px auto 22px;
  }

  .site-footer-line::before,
  .site-footer-line::after {
    width: 64px;
  }

  .site-footer-content {
    gap: 10px;
    font-size: 0.92rem;
    line-height: 1.8;
  }

  .honor-carousel-wrap {
    margin-top: 10px;
  }

  .honor-carousel ::v-deep .el-carousel__container {
    height: clamp(240px, 92vw, 520px);
  }

  .honor-nav {
    width: 34px;
    height: 34px;
    font-size: 22px;
  }

  ::v-deep .el-carousel__indicators--outside {
    bottom: 14px;
  }
}

@media (min-width: 769px) {
  .education-home {
    padding-bottom: 72px;
  }
}
</style>
